import { useEffect, useMemo, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Clients — HIPAA-clean directory. Per architecture:
 *   - Name, email, phone, status, sheets_on_file count → stored here
 *   - Notes (admin scratchpad) → stored here
 *   - Visit history, weights, doses, photos → paper charts (NEVER here)
 *
 * Read-only listing for now. Edit flow comes later.
 */

type Status = 'new' | 'active' | 'lapsed';

interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: Status;
  sheets_on_file: number;
  notes: string | null;
  created_at: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

const STATUS_OPTIONS: Status[] = ['new', 'active', 'lapsed'];
const STATUS_COLOR: Record<Status, { bg: string; fg: string }> = {
  new:    { bg: 'rgba(244, 201, 93, 0.18)',  fg: '#b87a1f' },
  active: { bg: 'rgba(127, 201, 122, 0.18)', fg: '#3f7a3a' },
  lapsed: { bg: 'rgba(170, 170, 170, 0.18)', fg: 'var(--text-muted)' },
};

function fmtSince(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = (await res.json()) as { clients: Client[] };
      setClients(data.clients ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (filter !== 'all' && c.status !== filter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q))
      );
    });
  }, [clients, filter, search]);

  const countByStatus = useMemo(() => {
    const out: Record<Status | 'all', number> = { all: clients.length, new: 0, active: 0, lapsed: 0 };
    for (const c of clients) out[c.status]++;
    return out;
  }, [clients]);

  return (
    <div style={{ maxWidth: 1000, display: 'grid', gap: 20 }}>
      <header>
        <h1 style={{ fontSize: 32, color: 'var(--primary)', margin: 0, fontFamily: 'var(--font-display)' }}>Clients</h1>
        <p style={{ color: 'var(--text-muted)', margin: '4px 0 0', maxWidth: 720 }}>
          Contact info + paper-chart references only. Clinical detail (visits, weights, doses, photos)
          lives on paper, not here.
        </p>
      </header>

      {error && (
        <div style={{ padding: 12, background: 'rgba(196, 69, 69, 0.08)', border: '1px solid rgba(196, 69, 69, 0.3)', borderRadius: 8, color: '#9a3535', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line-soft)' }}>
          {(['all', ...STATUS_OPTIONS] as const).map((s) => {
            const active = filter === s;
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(s)}
                style={{
                  background: 'transparent', border: 'none', padding: '10px 14px', fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--primary)' : 'var(--text-muted)',
                  borderBottom: active ? '2px solid var(--teal)' : '2px solid transparent',
                  marginBottom: -1, cursor: 'pointer', textTransform: 'capitalize',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {s}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{countByStatus[s]}</span>
              </button>
            );
          })}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, phone…"
          style={{
            fontFamily: 'inherit', fontSize: 13, padding: '8px 12px',
            border: '1px solid var(--line)', borderRadius: 8,
            background: 'var(--card)', color: 'var(--text)', outline: 'none',
            minWidth: 220, flex: '1 1 220px', maxWidth: 320,
          }}
        />
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : visible.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 12 }}>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>No clients in this view.</p>
        </div>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', textAlign: 'left' }}>
                <th style={th}>Name</th>
                <th style={th}>Contact</th>
                <th style={th}>Status</th>
                <th style={th}>Sheets</th>
                <th style={th}>Since</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <td style={{ ...td, fontWeight: 600, color: 'var(--primary)' }}>{c.name}</td>
                  <td style={{ ...td, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {c.email && <div>{c.email}</div>}
                    {c.phone && <div>{c.phone}</div>}
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '4px 10px', borderRadius: 999,
                        background: STATUS_COLOR[c.status].bg, color: STATUS_COLOR[c.status].fg,
                      }}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td style={{ ...td, fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 13 }}>
                    {c.sheets_on_file}
                  </td>
                  <td style={{ ...td, fontSize: 12.5, color: 'var(--text-muted)' }}>{fmtSince(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
};
const td: React.CSSProperties = {
  padding: '12px',
  verticalAlign: 'top',
};
